import { Grid, GridItem } from '@chakra-ui/react';
import { ProductApprovalState } from '@texas/api/endpoints/productApprovalsApi';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { getStateOption } from '@texas/components/article/productApproval/shared';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { sampleLifecycleGroupTitle } from '@texas/components/shared/samples/translations';
import {
  SampleLifecycleGroup,
  lifecycleToGroup,
} from '@texas/components/shared/samples/types';
import { ReactSelectOption } from '@texas/types';
import {
  convertToEnum,
  getEnumNamesAndValues,
  getEnumValues,
} from '@texas/utils/helpers/enumHelpers';
import { useTranslation } from 'react-i18next';
import {
  useCustomerProductApprovals,
  sampleLifecycles,
} from './CustomerProductApprovalsContext';

const stateOptions: ReactSelectOption[] = getEnumNamesAndValues(
  ProductApprovalState,
).map((s) => ({ label: s.name, value: s.value }));

export function CustomerProductApprovalFilters() {
  const { t } = useTranslation();
  const {
    searchQuery,
    setSearchQuery,
    customerFilters,
    loadingCustomerFilters,
  } = useCustomerProductApprovals();
  const branchOptions = customerFilters?.branches;
  const supplierOptions = customerFilters?.suppliers;
  const sampleStateOptions = getEnumValues<SampleLifecycleGroup>(
    SampleLifecycleGroup,
  ).map((group) => ({
    value: group,
    label: sampleLifecycleGroupTitle(group, t),
  }));
  return (
    <Grid
      templateColumns="repeat(8, 1fr)"
      backgroundColor="texas.bg.900"
      _light={{ backgroundColor: 'white' }}
      borderRadius="8"
      py={3}
      px={2}
      gap={4}
      alignItems="start"
    >
      <GridItem colSpan={2}>
        <TexasFormLabel marginBottom={0}>{t('general.search')}</TexasFormLabel>
        <SearchFilterInput
          value={searchQuery.searchTerm}
          placeholder={t('productApproval.searchNumberVariantOrArticle')}
          onChange={(s) =>
            setSearchQuery({ ...searchQuery, searchTerm: s, page: 1 })
          }
        />
      </GridItem>
      <GridItem>
        <TexasFormLabel>{t('general.state')}</TexasFormLabel>
        <TexasSelect
          placeholder={t('filter.allStates')}
          value={
            searchQuery.state ? getStateOption(searchQuery.state) : undefined
          }
          onChange={(e) =>
            setSearchQuery({
              ...searchQuery,
              state: convertToEnum(ProductApprovalState, e?.value),
              page: 1,
            })
          }
          options={stateOptions}
          isClearable={true}
        />
      </GridItem>
      <GridItem>
        <TexasFormLabel>{t('productApproval.sampleState')}</TexasFormLabel>
        <TexasSelect
          placeholder={t('filter.allSampleStates')}
          options={sampleStateOptions}
          value={sampleStateOptions.find(
            (option) =>
              option.value ===
              (searchQuery.sampleLifecycles?.[0] !== undefined
                ? lifecycleToGroup[searchQuery.sampleLifecycles[0]]
                : undefined),
          )}
          isClearable={true}
          onChange={(option) =>
            setSearchQuery({
              ...searchQuery,
              page: 1,
              sampleLifecycles: option
                ? sampleLifecycles.filter(
                    (lifecycle) => lifecycleToGroup[lifecycle] === option.value,
                  )
                : undefined,
            })
          }
        />
      </GridItem>
      <GridItem colSpan={2}>
        <TexasFormLabel>{t('general.branches')}</TexasFormLabel>
        <TexasSelect
          isLoading={loadingCustomerFilters}
          isClearable={true}
          isMulti={true}
          closeMenuOnSelect={false}
          value={searchQuery.branchIds?.map<ReactSelectOption>((x) => ({
            label: branchOptions?.find((b) => b.id === x)?.name ?? '',
            value: x,
          }))}
          onChange={(e) =>
            setSearchQuery({
              ...searchQuery,
              branchIds: e.map((x) => x.value),
              page: 1,
            })
          }
          options={branchOptions?.map<ReactSelectOption>((x) => ({
            value: x.id,
            label: x.name,
          }))}
        />
      </GridItem>
      <GridItem colSpan={2}>
        <TexasFormLabel>{t('general.suppliers')}</TexasFormLabel>
        <TexasSelect
          isLoading={loadingCustomerFilters}
          isClearable={true}
          isMulti={true}
          closeMenuOnSelect={false}
          value={searchQuery.supplierIds?.map<ReactSelectOption>((x) => ({
            label: supplierOptions?.find((b) => b.id === x)?.name ?? '',
            value: x,
          }))}
          onChange={(e) =>
            setSearchQuery({
              ...searchQuery,
              supplierIds: e.map((x) => x.value),
              page: 1,
            })
          }
          options={supplierOptions?.map<ReactSelectOption>((x) => ({
            value: x.id,
            label: x.name,
          }))}
        />
      </GridItem>
    </Grid>
  );
}
